// @ts-strict-ignore
import * as React from 'react';
import { Link } from 'react-router-dom';
import { AppComponentProps } from '../typescriptHelpers';

import type { ColumnsType } from 'antd/lib/table';
import {
    Integration,
    IntegrationInstallationId,
    IntegrationRefreshData,
    IntegrationRefreshStatus,
    IntegrationType,
    IntegrationCapability,
    IntegrationProviderCustomAnalytics,
    RefreshType,
} from '../types';
import { displayNameForIntegrationType, iconAndAltForIntegrationType } from '../helpers/integrationHelpers';
import Text from 'antd/lib/typography/Text';
import { dayjs } from '@/extendedDayjs';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import {
    selectAppIntegrations,
    selectAppTimeFormatSetting,
    selectEffectiveTimezone,
    selectOrgProviders,
} from '../selectors/selectors';
import { abbreviateTimeUnitAndRemoveLeadingSpace, DatetimeFormat, convertTo24HourFormat } from '../timeHelpers';
import {
    iconForIntegrationStatus,
    statusForIntegrationRefreshData,
    statusNodeForIntegrationStatus,
    buildIntegrationTooltipContent,
} from '../helpers/integrationRefreshDataHelpers';
import { antGrey, runwayColors, titleStyle } from './styles/globalStyles';
import { ApiOutlined, CloseOutlined, InfoCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { createAppSettingsIntegrationsRoute } from '../helpers/routeHelpers';
import { displayTextForIntegrationStatus, tagColorForIntegrationStatus } from '../helpers';
import { AppSettingsUpdatable, AppSettingsV2Updatable } from '../actions/updatable';
import { globalMessage } from '../utils/globalMessage';
import { tryUpdateAppSettings } from '../actions/appSettings';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Empty from 'antd/lib/empty';
import Divider from 'antd/lib/divider';
import Table from 'antd/lib/table';
import Button from 'antd/lib/button';
import Flex from 'antd/lib/flex';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const INTEGRATION_REFRESH_TIMEOUT_MS = 600000;
const STABILITY_INTEGRATION_REFRESH_TIMEOUT_MS = 1200000;

const stabilityRefreshTypeLabel = (rt: RefreshType): string | null => {
    switch (rt) {
        case RefreshType.StabilityInfo:
            return 'events';
        case RefreshType.StabilityIssues:
            return 'aggregate data';
        default:
            return null;
    }
};

// Firebase, Embrace, and Dynatrace have stub Issues fetches that complete with no data,
// so we hide the Issues row for any stability integration not listed here.
enum StabilityIssuesSupportedIntegration {
    Sentry = IntegrationType.Sentry,
    Bugsnag = IntegrationType.Bugsnag,
    Datadog = IntegrationType.DatadogStabilityMonitoring,
    NewRelic = IntegrationType.NewRelicStabilityMonitoring,
}

const stabilityMonitoringIntegrationSupportsIssues = (id: IntegrationType): boolean =>
    (Object.values(StabilityIssuesSupportedIntegration) as string[]).includes(id);

interface IntegrationRefreshRow {
    key: string;
    integration: Integration;
    refreshData: IntegrationRefreshData | null;
    displayNameSuffix: string | null;
}

const buildRefreshRows = (integrations: Integration[]): IntegrationRefreshRow[] => {
    const rows: IntegrationRefreshRow[] = [];
    for (const integration of integrations) {
        let refreshDataList = integration.refreshData ?? [];

        const isStabilityMonitoring = integration.capabilities?.includes(IntegrationCapability.StabilityMonitoring);
        if (isStabilityMonitoring && !stabilityMonitoringIntegrationSupportsIssues(integration.id)) {
            refreshDataList = refreshDataList.filter((rd) => rd.refreshType !== RefreshType.StabilityIssues);
        }

        if (refreshDataList.length <= 1) {
            rows.push({
                key: integration.installationId,
                integration,
                refreshData: refreshDataList[0] ?? null,
                displayNameSuffix: null,
            });
            continue;
        }

        for (const refreshData of refreshDataList) {
            rows.push({
                key: `${integration.installationId}:${refreshData.refreshType ?? ''}`,
                integration,
                refreshData,
                displayNameSuffix: refreshData.refreshType ? stabilityRefreshTypeLabel(refreshData.refreshType) : null,
            });
        }
    }
    return rows;
};

const integrationRefreshButtonTooltip = (refresh: IntegrationRefreshData | null) => {
    let header: string | null = null;
    let body: string | null = null;
    let headerColor: string | undefined = undefined;

    if (refresh?.inProgress) {
        const started = dayjs(refresh.refreshStartedAt);
        const now = dayjs();
        const elapsedMs = Math.max(0, now.diff(started));
        const hasTimedOut = elapsedMs > INTEGRATION_REFRESH_TIMEOUT_MS;

        if (!hasTimedOut) {
            header = 'Syncing...';
            headerColor = runwayColors.statusGreen;

            if (refresh.averageDurationMs) {
                const expectedRemainingMs = Math.max(0, refresh.averageDurationMs - elapsedMs);
                if (expectedRemainingMs === 0) {
                    body = `Sync started ${dayjs.duration(elapsedMs, 'milliseconds').humanize()} ago`;
                } else {
                    body = `Expected time remaining: ${dayjs.duration(expectedRemainingMs, 'milliseconds').humanize()}`;
                }
            } else {
                body = `Sync started ${dayjs.duration(elapsedMs, 'milliseconds').humanize()} ago`;
            }
        }
    }

    if (!refresh?.inProgress || !body) {
        if (refresh?.rateLimitHit) {
            header = 'Limit exceeded';
            headerColor = runwayColors.statusYellow;
            if (refresh.rateLimitResetAt) {
                const reset = dayjs(refresh.rateLimitResetAt);
                const now = dayjs();
                const remainingMs = Math.max(0, reset.diff(now));
                const minutes = Math.ceil(remainingMs / 60000);
                body = `Please try again in ${minutes} minutes`;
            } else {
                body = 'Please try again in a few minutes';
            }
        } else if (refresh && refresh.canTriggerManualRefresh === false) {
            body = "You do not have permission to trigger a refresh. Please contact your organization's admin";
        } else {
            body = 'Request sync';
        }
    }

    return { titleNode: buildIntegrationTooltipContent(header, body, headerColor) };
};

const IntegrationRefreshButton = (props: {
    stateKey: string;
    onTriggerRefreshIntegration: () => void;
    disabled?: boolean;
    isSyncing?: boolean;
    onSuccess?: () => void;
}) => {
    const { stateKey, onTriggerRefreshIntegration, disabled, isSyncing, onSuccess } = props;
    const triggerIntegrationRefreshIsLoading = useIsLoading(AppSettingsUpdatable.TriggerIntegrationRefresh, stateKey);
    const triggerIntegrationRefreshError = useError(AppSettingsUpdatable.TriggerIntegrationRefresh, stateKey);
    const triggerIntegrationRefreshSuccess = useUpdated(AppSettingsUpdatable.TriggerIntegrationRefresh, stateKey);

    React.useEffect(() => {
        if (triggerIntegrationRefreshError) {
            globalMessage.error(triggerIntegrationRefreshError);
        }
    }, [triggerIntegrationRefreshError]);

    React.useEffect(() => {
        if (triggerIntegrationRefreshSuccess) {
            globalMessage.success('Integration refresh kicked off!');
            if (onSuccess) onSuccess();
        }
    }, [triggerIntegrationRefreshSuccess]);

    return (
        <Button
            style={{
                height: 22,
            }}
            type="link"
            loading={triggerIntegrationRefreshIsLoading}
            disabled={disabled}
            icon={<SyncOutlined spin={!!isSyncing} />}
            onClick={onTriggerRefreshIntegration}
        />
    );
};

const IntegrationRefreshPopover = (props: AppComponentProps) => {
    const integrationRefreshIsLoading = useIsLoading(AppSettingsV2Updatable.RefreshIntegrationRefreshData, props.appId);
    const integrationRefreshError = useError(AppSettingsV2Updatable.RefreshIntegrationRefreshData, props.appId);

    const dispatch = useDispatch();

    const integrations = useSelector((state: RootState) => {
        // always show comms last
        const integrationsList = selectAppIntegrations(state, props.appId);
        return [...integrationsList].sort((a, b) => {
            if (a.id === IntegrationType.Slack || a.id === IntegrationType.MicrosoftTeams) {
                return 1;
            } else if (b.id === IntegrationType.Slack || b.id === IntegrationType.MicrosoftTeams) {
                return -1;
            }

            return 0;
        });
    });

    const refreshRows = React.useMemo(() => buildRefreshRows(integrations), [integrations]);

    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));
    const integrationProviders = useSelector((state: RootState) => selectOrgProviders(state, props.orgId));

    React.useEffect(() => {
        if (integrationRefreshError) {
            globalMessage.error(integrationRefreshError);
        }
    }, [integrationRefreshError]);

    const triggerRefreshIntegration = (
        integrationId: IntegrationInstallationId,
        refreshType: RefreshType | undefined,
        stateKey: string,
    ) => {
        dispatch(
            tryUpdateAppSettings(
                AppSettingsUpdatable.TriggerIntegrationRefresh,
                {
                    orgId: props.orgId,
                    appId: props.appId,
                    integrationId,
                    refreshType,
                },
                stateKey,
            ),
        );
    };

    const columns: ColumnsType<IntegrationRefreshRow> = [
        {
            title: 'Integration',
            render: (_value: unknown, row: IntegrationRefreshRow) => {
                const { integration, displayNameSuffix } = row;
                const integrationStatus = statusForIntegrationRefreshData(integration.integrationError);
                const [icon, alt] = iconAndAltForIntegrationType(integration.id);

                var displayName = integration.alias || displayNameForIntegrationType(integration.id);
                if (!integration.alias && integration.id === IntegrationType.CustomAnalytics) {
                    const provider = integrationProviders.find(
                        (provider) => provider.id === integration.providerId,
                    ) as IntegrationProviderCustomAnalytics;
                    if (provider?.data?.displayableName) {
                        displayName = provider.data.displayableName;
                    }
                }

                if (displayNameSuffix) {
                    displayName = `${displayName} ${displayNameSuffix}`;
                }

                return (
                    <Space align="center">
                        {statusNodeForIntegrationStatus(integrationStatus)}
                        {icon && <img src={icon} alt={alt} width={24} height={24} />}
                        <Text style={{ fontSize: 14 }}>{displayName}</Text>
                    </Space>
                );
            },
        },
        {
            title: 'Refresh Data',
            align: 'right',
            render: (_value: unknown, row: IntegrationRefreshRow) => {
                const { integration, refreshData, key: rowKey } = row;
                const showRefreshForIntegration =
                    integration.id !== IntegrationType.Slack && integration.id !== IntegrationType.MicrosoftTeams;

                let lastRefreshPopoverTimeString = '--';
                let integrationStatus = statusForIntegrationRefreshData(integration.integrationError);
                let integrationStatusIcon = iconForIntegrationStatus(integrationStatus);

                const lastRefreshedAt = refreshData?.lastRefreshedAt ?? null;

                if (showRefreshForIntegration) {
                    if (lastRefreshedAt) {
                        const lastRefreshTimeMoment = dayjs(lastRefreshedAt);
                        lastRefreshPopoverTimeString = abbreviateTimeUnitAndRemoveLeadingSpace(
                            lastRefreshTimeMoment.fromNow(),
                        );
                    } else {
                        // show warning icon if no last refresh timestamp
                        integrationStatus = IntegrationRefreshStatus.Warning;
                        integrationStatusIcon = (
                            <InfoCircleOutlined style={{ color: runwayColors.darkTextGrey, fontSize: 12 }} />
                        );
                    }
                }

                const isStabilityMonitoring = integration.capabilities?.includes(
                    IntegrationCapability.StabilityMonitoring,
                );
                const timeoutMs =
                    isStabilityMonitoring ? STABILITY_INTEGRATION_REFRESH_TIMEOUT_MS : INTEGRATION_REFRESH_TIMEOUT_MS;
                const hasTimedOut =
                    refreshData?.inProgress && refreshData.refreshStartedAt ?
                        dayjs().diff(dayjs(refreshData.refreshStartedAt)) > timeoutMs
                    :   false;
                const isSyncing = Boolean(refreshData?.inProgress) && !hasTimedOut;
                const userCanTriggerRefresh = refreshData?.canTriggerManualRefresh ?? false;
                const canShowRefresh = showRefreshForIntegration && userCanTriggerRefresh;
                const isDisabled = Boolean(
                    refreshData &&
                        ((refreshData.inProgress && !hasTimedOut) ||
                            refreshData.rateLimitHit ||
                            refreshData.canTriggerManualRefresh === false),
                );

                const infoTooltip = (() => {
                    const headerText = displayTextForIntegrationStatus(integration);
                    const headerColor = tagColorForIntegrationStatus(integration);
                    const localTimezone = dayjs.tz.guess();
                    const isLocalTimezone = effectiveTimezone === localTimezone;
                    const shortTz =
                        effectiveTimezone && !isLocalTimezone ?
                            ` ${dayjs.tz(dayjs(lastRefreshedAt), effectiveTimezone).format('z')}`
                        :   '';
                    const bodyText =
                        lastRefreshedAt ?
                            `Last refresh at ${dayjs(lastRefreshedAt)
                                .tz(effectiveTimezone)
                                .format(
                                    use24HourTimeFormat ?
                                        convertTo24HourFormat(DatetimeFormat.ShortMonthDateTime)
                                    :   DatetimeFormat.ShortMonthDateTime,
                                )}${shortTz}`
                        :   'Last refresh time unavailable';
                    return buildIntegrationTooltipContent(headerText, bodyText, headerColor);
                })();

                const timeoutTooltip = buildIntegrationTooltipContent(
                    'Sync timeout',
                    'The sync took longer than expected. Please try again',
                    runwayColors.statusRed,
                );

                return (
                    <Flex justify="flex-end" align="center">
                        {isSyncing ?
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Syncing...
                            </Text>
                        : hasTimedOut ?
                            <Space>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Sync timeout
                                </Text>
                                <Tooltip color={antGrey.grey9} placement="right" title={timeoutTooltip}>
                                    <CloseOutlined style={{ color: runwayColors.statusRed, fontSize: 12 }} />
                                </Tooltip>
                            </Space>
                        :   <Tooltip color={antGrey.grey9} placement="right" title={infoTooltip}>
                                <Space>
                                    <Text style={{ fontSize: 12 }} ellipsis>
                                        {lastRefreshPopoverTimeString}
                                    </Text>
                                    {integrationStatusIcon}
                                </Space>
                            </Tooltip>
                        }
                        {canShowRefresh ?
                            <>
                                <Divider type="vertical" />
                                <Tooltip
                                    color={antGrey.grey9}
                                    placement="right"
                                    title={integrationRefreshButtonTooltip(refreshData).titleNode}
                                >
                                    <span style={{ display: 'inline-block' }}>
                                        <IntegrationRefreshButton
                                            stateKey={rowKey}
                                            isSyncing={isSyncing}
                                            disabled={isDisabled}
                                            onTriggerRefreshIntegration={() => {
                                                triggerRefreshIntegration(
                                                    integration.installationId,
                                                    refreshData?.refreshType,
                                                    rowKey,
                                                );
                                            }}
                                        />
                                    </span>
                                </Tooltip>
                            </>
                        :   <>
                                <Divider type="vertical" style={{ visibility: 'hidden' }} />
                                <Button
                                    type="link"
                                    style={{ visibility: 'hidden', height: 22 }}
                                    icon={<SyncOutlined />}
                                />
                            </>
                        }
                    </Flex>
                );
            },
        },
    ];

    const appSettingsIntegrationRoute = createAppSettingsIntegrationsRoute(props.orgId, props.appId);

    const empty = (
        <Empty
            image={<ApiOutlined style={{ color: antGrey.grey5, fontSize: 38 }} />}
            imageStyle={{ height: 38 }}
            description={
                <div style={{ color: antGrey.grey7, fontSize: 16 }}>
                    Go to <Link to={{ pathname: appSettingsIntegrationRoute }}>Settings &gt; Integrations</Link>
                    <br />
                    to connect your tools
                </div>
            }
            style={{ padding: 20 }}
        />
    );

    if (integrations.length === 0) {
        return empty;
    } else {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: 6,
                        paddingBottom: 6,
                        paddingLeft: 12,
                        paddingRight: 12,
                    }}
                >
                    <Text style={{ ...titleStyle, fontSize: 11 }}>Integrations</Text>
                    <Link to={{ pathname: appSettingsIntegrationRoute }}>View all</Link>
                </div>
                <Divider style={{ width: '100%', padding: 0, margin: 0 }} />
                <div style={{ maxHeight: '50rem', overflowY: 'auto' }}>
                    <Table
                        loading={integrationRefreshIsLoading}
                        showHeader={false}
                        pagination={false}
                        bordered={false}
                        columns={columns}
                        dataSource={refreshRows}
                        rowKey="key"
                        size="small"
                        className="integration-refresh-table"
                    />
                </div>
            </div>
        );
    }
};

export default IntegrationRefreshPopover;
